/**
 * HustlePlaza Shop — real-data storefront wired to /api/dropshipzone
 * and /api/plaza/smart with full Shopify/Amazon-class UX.
 *
 * Routes:
 *   /plaza/shop          — catalog grid + filters + cart drawer
 *   /plaza/shop?sku=…    — opens product detail modal for that SKU
 *
 * Features:
 *   • Real DSZ products (no mocks)
 *   • Preorder badge with "Back in stock by {date}" copy
 *   • Recently viewed carousel (per session)
 *   • You-might-also-like recommendations on PDP
 *   • Notify-me-when-back-in-stock CTA
 *   • Save-for-later / Wishlist (synced to /api/plaza/smart/wishlist)
 *   • Cart with free-shipping progress bar (Amazon-class)
 *   • Cart-recovery banner if you arrived from a recovery email
 *     (?recover=1 query param)
 *   • Product reviews & ratings with histogram
 *   • Wallet credit display from HustleEats Thank-You wallet
 *   • View-tracking POST on every card open
 *   • Cart-event tracking on add/remove/checkout_start
 *
 * Component breakdown (post-Phase-42 refactor, see `/app/frontend/src/components/plaza/`):
 *   ShopTopBar, CategoryRail, PromoBanner, AmazonFallbackCard, DiscoveryRails,
 *   SmartProductCard, Carousel, ProductDetailModal, CartDrawer.
 */
import { useEffect, useMemo, useState, useCallback, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, Loader2, CheckCircle2 } from "lucide-react";

import {
  api, sm, loadCart, saveCart,
} from "../components/plaza/plazaLib";
import ShopTopBar from "../components/plaza/ShopTopBar";
import CategoryRail from "../components/plaza/CategoryRail";
import PlazaAeo from "../components/plaza/PlazaAeo";
import PromoBanner from "../components/plaza/PromoBanner";
import AmazonFallbackCard from "../components/plaza/AmazonFallbackCard";
import DiscoveryRails from "../components/plaza/DiscoveryRails";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import SmartProductCard from "../components/plaza/SmartProductCard";
import ProductListTable from "../components/plaza/ProductListTable";
import PlazaPagination from "../components/plaza/PlazaPagination";
import Carousel from "../components/plaza/Carousel";
import HolidayBanner from "../components/plaza/HolidayBanner";
import HolidayEffects from "../components/plaza/HolidayEffects";
import { useHolidayTheme } from "../hooks/useHolidayTheme";

// Lazy-loaded — only fetched on first interaction (PDP open / cart open).
// Saves ~20–25 KB off first-paint bundle.
const ProductDetailModal = lazy(() =>
  import("../components/plaza/ProductDetailModal"));
const CartDrawer = lazy(() =>
  import("../components/plaza/CartDrawer"));

import "./HustlePlazaPage.css";
import "./HustlePlazaShopPage.css";

export default function HustlePlazaShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [activeSku, setActiveSku] = useState(null);
  const [cart, setCart] = useState(loadCart());
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Keep CartDrawer unmounted (= bundle not fetched) until the very first
  // open. After that we leave it in the DOM so the slide-out animation
  // works on subsequent close → open cycles.
  const [drawerEverOpened, setDrawerEverOpened] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [topSearched, setTopSearched] = useState([]);
  const [topPurchased, setTopPurchased] = useState([]);
  const [trendingCats, setTrendingCats] = useState([]);
  const [activePromo, setActivePromo] = useState(null);
  const [shipping, setShipping] = useState({ enabled: true, threshold_cents: 9900 });
  const [wallet, setWallet] = useState({ balance_cents: 0 });
  const [catalogCount, setCatalogCount] = useState({ live: 0, total: 0 });

  // Live autocomplete suggestions.
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestHover, setSuggestHover] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const recover = searchParams.get("recover") === "1";
  const holiday = useHolidayTheme();

  // View mode + paging + sorting
  const [view, setView] = useState("list");           // 'list' | 'grid'
  const [listSize, setListSize] = useState(25);       // 25/50/100/250/500
  const [gridSize, setGridSize] = useState(15);       // 15/30/45/90/180
  const [sortBy, setSortBy] = useState(null);         // title|price|brand|availability
  const [sortDir, setSortDir] = useState("asc");
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);                // list-mode 1-indexed page
  const pageLimit = view === "list" ? listSize : gridSize;
  const totalLive = catalogCount.live || 0;
  const totalPages = Math.max(1, Math.ceil(totalLive / listSize));

  // Initial load
  useEffect(() => {
    Promise.all([
      sm.listProducts({ availability: "live", limit: pageLimit }),
      sm.listCategories().catch(() => []),
      sm.freeShipping(),
      sm.wallet().catch(() => ({ balance_cents: 0 })),
      sm.wishlist().catch(() => []),
      sm.recentlyViewed().catch(() => []),
      sm.topSearched().catch(() => []),
      sm.topPurchased().catch(() => []),
      sm.trendingCategories().catch(() => []),
      sm.activePromo().catch(() => ({ active: false })),
      sm.catalogCount().catch(() => ({ live: 0, total: 0 })),
    ]).then(([prods, cats, ship, w, wl, rv, ts, tp, tc, promo, cnt]) => {
      setProducts(prods);
      setHasMore(prods.length >= pageLimit);
      setCategories(cats);
      setShipping(ship);
      setWallet(w);
      setWishlist(wl);
      setRecentlyViewed(rv);
      setTopSearched(ts);
      setTopPurchased(tp);
      setTrendingCats(tc);
      setActivePromo(promo?.active ? promo.promo : null);
      setCatalogCount(cnt);
      setLoading(false);
    });
    const sku = searchParams.get("sku");
    if (sku) setActiveSku(sku);
     
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 320);
    return () => clearTimeout(t);
  }, [query]);

  // Live autocomplete — 150 ms debounce so the dropdown feels instant.
  // Uses the /suggest endpoint (title-only, tiny payload, backed by the
  // same in-process 60 s cache as /search).
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      sm.suggest(q, 6).then((d) => {
        setSuggestions((d && d.results) || []);
      });
    }, 150);
    return () => clearTimeout(t);
  }, [query]);

  const [amazonFallback, setAmazonFallback] = useState(null);

  // Re-fetch when filter changes — fuzzy search when there's a query
  useEffect(() => {
    setLoading(true);
    if (debouncedQuery && debouncedQuery.length >= 2) {
      sm.fuzzySearch(debouncedQuery, activeCat).then(d => {
        const list = (d.results || []).map(r => ({
          ...r,
          images: r.image_url ? [{ public_url: r.image_url }] : [],
          selling_price_cents: r.effective_price_cents || r.selling_price_cents,
        }));
        setProducts(list);
        setLoading(false);
        if (list.length < 4 && debouncedQuery.length >= 3) {
          sm.amazonLookup(debouncedQuery).then((res) => {
            if (res && res.found) setAmazonFallback({ q: debouncedQuery, ...res });
            else setAmazonFallback(null);
          });
        } else {
          setAmazonFallback(null);
        }
      }).catch(() => setLoading(false));
    } else {
      setAmazonFallback(null);
      // List mode uses page/skip. Grid mode fetches page 1 fresh (append-on-load-more).
      const skip = view === "list" ? (page - 1) * listSize : 0;
      sm.listProducts({
        availability: "live",
        limit: pageLimit,
        skip,
        ...(activeCat ? { category_id: activeCat } : {}),
        ...(sortBy ? { sort_by: sortBy, sort_dir: sortDir } : {}),
      }).then(p => {
        setProducts(p);
        setHasMore(p.length >= pageLimit);
        setLoading(false);
      });
    }
  }, [debouncedQuery, activeCat, pageLimit, sortBy, sortDir, page, view, listSize]);

  // Reset to page 1 when filters/search change so we don't sit on an
  // empty page beyond the last result.
  useEffect(() => { setPage(1); }, [debouncedQuery, activeCat, listSize, sortBy, sortDir]);

  // "Show more" — grid-mode only, appends the next page-size worth of items.
  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const more = await sm.listProducts({
        availability: "live",
        limit: gridSize,
        skip: products.length,
        ...(activeCat ? { category_id: activeCat } : {}),
        ...(sortBy ? { sort_by: sortBy, sort_dir: sortDir } : {}),
      });
      setProducts(prev => {
        const seen = new Set(prev.map(p => p.sku));
        return [...prev, ...more.filter(p => !seen.has(p.sku))];
      });
      setHasMore(more.length === gridSize);
    } finally {
      setLoadingMore(false);
    }
  }, [products.length, activeCat, sortBy, sortDir, gridSize]);

  const onSort = useCallback((key) => {
    setSortBy(prev => {
      if (prev === key) { setSortDir(d => (d === "asc" ? "desc" : "asc")); return prev; }
      setSortDir("asc");
      return key;
    });
  }, []);

  // Search results come pre-ranked from fuzzy search — sort client-side.
  const displayProducts = useMemo(() => {
    if (!sortBy || !debouncedQuery) return products;
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (p) => sortBy === "price"
      ? (p.selling_price_cents || p.rrp_cents || 0)
      : String(p[sortBy] || "").toLowerCase();
    return [...products].sort((a, b) => (val(a) > val(b) ? dir : val(a) < val(b) ? -dir : 0));
  }, [products, sortBy, sortDir, debouncedQuery]);

  // Social-proof hydration — batched, throttled, refreshed on visible-set change.
  const [proofByS, setProofByS] = useState({});
  useEffect(() => {
    const skus = displayProducts.map((p) => p.sku).filter(Boolean);
    if (!skus.length) return;
    let cancelled = false;
    sm.socialProof(skus, 7).then((d) => {
      if (!cancelled) setProofByS(d.counts || {});
    });
    return () => { cancelled = true; };
  }, [displayProducts]);

  // Cart persistence
  useEffect(() => { saveCart(cart); }, [cart]);

  const subtotalCents = useMemo(
    () => cart.reduce((s, l) => s + (l.price_cents * l.qty), 0), [cart]);
  const cartCount = cart.reduce((n, l) => n + l.qty, 0);

  const addToCart = useCallback((product, qty = 1) => {
    setCart(prev => {
      const ix = prev.findIndex(l => l.sku === product.sku);
      if (ix >= 0) {
        const next = [...prev];
        next[ix] = { ...next[ix], qty: next[ix].qty + qty };
        return next;
      }
      return [...prev, {
        sku: product.sku,
        title: product.title,
        price_cents: product.selling_price_cents || product.rrp_cents || 0,
        image_url: (product.images || [{}])[0]?.public_url,
        availability: product.availability,
        restock_eta_iso: product.restock_eta_iso,
        days_until_restock: product.days_until_restock,
        qty,
      }];
    });
    sm.trackCart(product.sku, "add", qty);
    sm.signal({
      action: "add", sku: product.sku,
      category: product.category_path,
      cents: product.selling_price_cents || 0,
    });
    setDrawerEverOpened(true);
    setDrawerOpen(true);
  }, []);

  const removeLine = (sku) => {
    setCart(c => c.filter(l => l.sku !== sku));
    sm.trackCart(sku, "remove");
    sm.signal({ action: "abandon", sku });
  };
  const updateQty = (sku, qty) => {
    if (qty <= 0) return removeLine(sku);
    setCart(c => c.map(l => l.sku === sku ? { ...l, qty } : l));
  };
  const clearCart = () => setCart([]);

  /* ─── Checkout — POSTs to /api/payments/checkout ─── */
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const onCheckout = useCallback(async () => {
    if (!cart.length) return;
    setCheckoutBusy(true);
    cart.forEach(l => sm.trackCart(l.sku, "checkout_start", l.qty));
    sm.signal({ action: "click", meta: { trigger: "checkout_start",
                                          line_count: cart.length,
                                          subtotal_cents: subtotalCents } });
    try {
      const items = cart.map(l => ({
        sku: l.sku, qty: l.qty,
        cents: l.selling_price_cents || l.effective_price_cents || 0,
      }));
      const body = {
        items,
        shipping_address: {
          name: "Plaza Shopper",
          line1: "TBC at fulfilment",
          city: "Brisbane", state: "QLD",
          postcode: "4000", country: "AU",
        },
        freight_cents: 0,
        buyer_email: "checkout-tbd@hustleplaza.au",
        wallet_redeem_cents: 0,
      };
      const r = await api.post(`/api/payments/checkout`, body);
      if (r.data.fully_paid_by_wallet) {
        clearCart();
        alert(`Order placed! ID: ${r.data.order_id}\nFully paid by Hustle Dollars 💵`);
      } else {
        alert(
          `Order created: ${r.data.order_id}\n` +
          `Amount: $${(r.data.amount_cents/100).toFixed(2)}\n` +
          `Wallet applied: $${(r.data.wallet_applied_cents/100).toFixed(2)}\n\n` +
          `Stripe PaymentElement UI will mount in the next sprint using ` +
          `the client_secret. The order is in the database; the webhook ` +
          `will release it to DSZ once payment confirms.`
        );
        clearCart();
      }
    } catch (e) {
      alert(`Checkout error: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCheckoutBusy(false);
    }
  }, [cart, subtotalCents]);

  const toggleWishlist = useCallback(async (sku) => {
    const res = await sm.wishlistToggle(sku);
    sm.wishlist().then(setWishlist);
    return res.saved;
  }, []);
  const inWishlist = (sku) => wishlist.some(w => w.sku === sku);

  const openProduct = (sku) => {
    setActiveSku(sku);
    setSearchParams({ sku });
    sm.trackView(sku);
  };
  const closeProduct = () => {
    setActiveSku(null);
    const next = new URLSearchParams(searchParams);
    next.delete("sku");
    setSearchParams(next);
    sm.recentlyViewed().then(setRecentlyViewed);
  };

  return (
    <div className="hpz-shop hpz-light" data-testid="plaza-shop-root">
      <PlatformHeader
        brand={{ name: "HustlePlaza Shop", to: "/plaza/shop" }}
        showBookCta={false}
        showCart={false}
      />
      <ShopTopBar
        cartCount={cartCount}
        onOpenCart={() => { setDrawerEverOpened(true); setDrawerOpen(true); }}
        wallet={wallet}
      />

      <HolidayBanner
        theme={holiday.theme}
        dismissed={holiday.dismissed}
        onDismiss={holiday.dismiss}
        isPreview={holiday.isPreview}
        cycleInfo={holiday.cycleInfo}
      />

      <HolidayEffects theme={holiday.theme} />

      {activePromo && <PromoBanner promo={activePromo} />}

      {recover && (
        <div className="hpz-shop__recover-banner" data-testid="plaza-recovery-banner">
          <CheckCircle2 size={18} />
          <span>Welcome back — your cart is still here. Pick up where you left off.</span>
          <button onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.delete("recover");
            setSearchParams(next);
          }} data-testid="plaza-recovery-dismiss" aria-label="Dismiss"><X size={16} /></button>
        </div>
      )}

      <div className="hpz-shop__hero">
        <h1>HustlePlaza Marketplace</h1>
        <p className="hpz-shop__hero-sub">
          {catalogCount.live > 0 ? (
            <>Browse <b>{catalogCount.live.toLocaleString("en-AU")}</b>
            {" "}Australian-warehoused items</>
          ) : (
            <>Member-priced wholesale catalog</>
          )}
          {" · "}Member-priced · Free shipping over ${(shipping.threshold_cents/100).toFixed(0)}
        </p>
        <div className="hpz-shop__search" style={{ position: "relative" }}>
          <Search size={18} />
          <input
            placeholder={catalogCount.live > 0
              ? `Search ${catalogCount.live.toLocaleString("en-AU")} items…`
              : "Search the catalog"}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSuggestOpen(true); }}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => setTimeout(() => {
              if (!suggestHover) setSuggestOpen(false);
            }, 150)}
            data-testid="plaza-search-input"
          />
          {suggestOpen && suggestions.length > 0 && (
            <div
              data-testid="plaza-search-suggestions"
              onMouseEnter={() => setSuggestHover(true)}
              onMouseLeave={() => setSuggestHover(false)}
              style={{
                position: "absolute", top: "100%", left: 0, right: 0,
                marginTop: 6, background: "#fff",
                border: "1px solid #e5e0d1", borderRadius: 12,
                boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                overflow: "hidden", zIndex: 20,
              }}
            >
              {suggestions.slice(0, 6).map((s) => (
                <button
                  key={s.sku}
                  type="button"
                  data-testid={`plaza-suggest-${s.sku}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setQuery(s.title);
                    setSuggestOpen(false);
                    setSuggestHover(false);
                  }}
                  style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", width: "100%",
                    padding: "10px 14px", background: "transparent",
                    border: 0, borderBottom: "1px solid #f4efe0",
                    textAlign: "left", cursor: "pointer",
                    fontSize: 13, color: "#1c1c1c",
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = "#faf7ea"}
                  onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <span style={{
                    overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", flex: 1, paddingRight: 12,
                  }}>{s.title}</span>
                  {s.cents ? (
                    <span style={{ fontWeight: 700, color: "#8a5b1f",
                                   whiteSpace: "nowrap" }}>
                      ${(s.cents / 100).toFixed(2)}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="hpz-shop__layout">
        <CategoryRail
          categories={categories}
          active={activeCat}
          onPick={setActiveCat}
        />

        <main className="hpz-shop__main">
          {!debouncedQuery && !activeCat && (
            <DiscoveryRails
              topSearched={topSearched}
              topPurchased={topPurchased}
              trendingCats={trendingCats}
              onPickQuery={(q) => setQuery(q)}
              onPickCategory={(cid) => setActiveCat(cid)}
              onOpen={openProduct}
              onAdd={addToCart}
              inWishlist={inWishlist}
              onToggleWishlist={toggleWishlist}
            />
          )}

          {recentlyViewed.length > 0 && !debouncedQuery && !activeCat && (
            <Carousel
              title="Recently viewed"
              testid="plaza-recently-viewed"
              products={recentlyViewed}
              onOpen={openProduct}
              onAdd={addToCart}
              inWishlist={inWishlist}
              onToggleWishlist={toggleWishlist}
            />
          )}

          {loading ? (
            <div className="hpz-shop__loading" data-testid="plaza-loading">
              <Loader2 className="hpz-spin" size={32} />
              <p>Loading the catalog…</p>
            </div>
          ) : products.length === 0 ? (
            <div className="hpz-shop__empty" data-testid="plaza-empty">
              <p>No products match — try a different search or category.</p>
            </div>
          ) : (
            <>
              <PlazaPagination
                view={view}
                onView={setView}
                pageSize={pageLimit}
                onPageSize={(n) => view === "list" ? setListSize(n) : setGridSize(n)}
                pageSizeOptions={view === "list"
                  ? [25, 50, 100, 250, 500]
                  : [15, 30, 45, 90, 180]}
                shown={displayProducts.length}
                total={debouncedQuery ? displayProducts.length : (catalogCount.live || displayProducts.length)}
                page={page}
                totalPages={debouncedQuery ? 1 : totalPages}
                onPage={setPage}
                hasMore={!debouncedQuery && hasMore}
                loadingMore={loadingMore}
                onLoadMore={loadMore}
                testidPrefix="plaza-pagi"
              />

              {view === "list" ? (
                <ProductListTable
                  products={displayProducts}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                  onOpen={openProduct}
                  onAdd={(p) => addToCart(p)}
                />
              ) : (
                <div className="hpz-shop__grid" data-testid="plaza-product-grid">
                  {displayProducts.map((p) => (
                    <SmartProductCard
                      key={p.sku}
                      product={p}
                      onOpen={() => openProduct(p.sku)}
                      onAdd={() => addToCart(p)}
                      inWishlist={inWishlist(p.sku)}
                      onToggleWishlist={() => toggleWishlist(p.sku)}
                      proof={proofByS[p.sku]}
                    />
                  ))}
                </div>
              )}

              <PlazaPagination
                view={view}
                onView={setView}
                showViewToggle={false}
                pageSize={pageLimit}
                onPageSize={(n) => view === "list" ? setListSize(n) : setGridSize(n)}
                pageSizeOptions={view === "list"
                  ? [25, 50, 100, 250, 500]
                  : [15, 30, 45, 90, 180]}
                shown={displayProducts.length}
                total={debouncedQuery ? displayProducts.length : (catalogCount.live || displayProducts.length)}
                page={page}
                totalPages={debouncedQuery ? 1 : totalPages}
                onPage={setPage}
                hasMore={!debouncedQuery && hasMore}
                loadingMore={loadingMore}
                onLoadMore={loadMore}
                testidPrefix="plaza-pagi-bottom"
              />
            </>
          )}

          {amazonFallback && amazonFallback.result && (
            <AmazonFallbackCard
              q={amazonFallback.q}
              result={amazonFallback.result}
              onClose={() => setAmazonFallback(null)}
            />
          )}
        </main>
      </div>

      {activeSku && (
        <Suspense fallback={null}>
          <ProductDetailModal
            sku={activeSku}
            onClose={closeProduct}
            onAdd={addToCart}
            inWishlist={inWishlist(activeSku)}
            onToggleWishlist={() => toggleWishlist(activeSku)}
            onSwitchSku={setActiveSku}
          />
        </Suspense>
      )}

      <PlazaAeo
        category={categories.find((c) => c.category_id === activeCat) || null}
        products={products}
        product={products.find((p) => p.sku === activeSku) || null}
      />

      {drawerEverOpened && (
        <Suspense fallback={null}>
          <CartDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            lines={cart}
            onUpdateQty={updateQty}
            onRemove={removeLine}
            onClear={clearCart}
            subtotalCents={subtotalCents}
            shipping={shipping}
            wallet={wallet}
            onCheckout={onCheckout}
            checkoutBusy={checkoutBusy}
          />
        </Suspense>
      )}
      <SuiteFooter />
    </div>
  );
}
